import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

/* Eager — lightweight, always needed */
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";

/* Lazy — heavy pages loaded on demand */
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const HealthPage = lazy(() => import("@/pages/HealthPage"));
const DisclaimerPage = lazy(() => import("@/pages/DisclaimerPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const AdminGate = lazy(() => import("@/components/AdminGate"));
const EngagementGate = lazy(() => import("@/components/EngagementGate"));

function PageLoader() {
  return (
    <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center gap-3">
      <Loader2 className="w-7 h-7 animate-spin text-[#B48C36]" />
      <div className="space-y-3 w-[90%] max-w-md animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
        <div className="h-10 bg-gray-100 rounded" />
        <div className="h-10 bg-gray-100 rounded" />
        <div className="h-10 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Protected — regular user (EngagementGate = removable progressive forms) */}
          <Route path="/dashboard" element={<ProtectedRoute><EngagementGate><Dashboard /></EngagementGate></ProtectedRoute>} />
          <Route path="/compare" element={<ProtectedRoute><EngagementGate><ComparePage /></EngagementGate></ProtectedRoute>} />

          {/* Protected — admin credentials only (no user login required) */}
          <Route path="/sysbook" element={<AdminGate><BookingPage /></AdminGate>} />
          <Route path="/sysadmin" element={<AdminGate><AdminPage /></AdminGate>} />
          <Route path="/sysadmin/analytics" element={<AdminGate><AnalyticsPage /></AdminGate>} />
          <Route path="/sysadmin/health" element={<AdminGate><HealthPage /></AdminGate>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
